import api from './api';

export interface Rack {
  id: number;
  number: number;
  label: string;
  is_active: boolean;
  aruco_ids: number[];
  is_calibrated: boolean;
  needs_recalibration: boolean;
  drift_total: number;
  calibrated_at: string | null;
  calibrated_by_name: string | null;
  pan_raw: number | null;
  tilt_raw: number | null;
  zoom_raw: number | null;
  focus_raw: number | null;
  focus_auto: boolean;
  /** élève installé à cette place (check-in par le QR de la place), s'il y en a un */
  current_student: string | null;
  checked_in_at: string | null;
}

export type CaptureStatus = 'queued' | 'running' | 'done' | 'failed';

export interface RackCapture {
  id: number;
  rack: number;
  rack_number: number;
  kind: 'calibration' | 'test' | 'view' | 'correction';
  status: CaptureStatus;
  failure_reason: string;
  detail: string;
  photo_raw_url: string | null;
  photo_rectified_url: string | null;
  drift_px: number | null;
  recentered: boolean;
  created_at: string;
  finished_at: string | null;
}

export interface CameraPosition {
  pan: number;
  tilt: number;
  zoom: number;
  focus: number;
  focus_auto: boolean;
}

export interface CameraMove {
  pan_delta?: number;
  tilt_delta?: number;
  zoom?: number;
  focus?: number;
  focus_auto?: boolean;
  one_push_af?: boolean;
}

class RackService {
  async list(): Promise<Rack[]> {
    return (await api.get('/camera/racks/')).data;
  }

  async create(data: { number: number; label?: string }): Promise<Rack> {
    return (await api.post('/camera/racks/', data)).data;
  }

  async update(id: number, data: Partial<Pick<Rack, 'label' | 'is_active'>>): Promise<Rack> {
    return (await api.patch(`/camera/racks/${id}/`, data)).data;
  }

  async remove(id: number): Promise<void> {
    await api.delete(`/camera/racks/${id}/`);
  }

  async calibrate(id: number): Promise<RackCapture> {
    return (await api.post(`/camera/racks/${id}/calibrate/`)).data;
  }

  async test(id: number): Promise<RackCapture> {
    return (await api.post(`/camera/racks/${id}/test/`)).data;
  }

  /** Photo de ce qu'il y a en ce moment à la position enregistrée du rack (sans recentrage). */
  async view(id: number): Promise<RackCapture> {
    return (await api.post(`/camera/racks/${id}/view/`)).data;
  }

  async capture(id: number): Promise<RackCapture> {
    return (await api.get(`/camera/captures/${id}/`)).data;
  }

  async snapshot(): Promise<Blob> {
    return (await api.get('/camera/live/snapshot/', { responseType: 'blob' })).data;
  }

  async position(): Promise<CameraPosition> {
    return (await api.get('/camera/live/position/')).data;
  }

  async move(move: CameraMove): Promise<CameraPosition> {
    return (await api.post('/camera/live/move/', move)).data;
  }

  async markersPdf(): Promise<Blob> {
    return (await api.get('/camera/racks/markers/', { responseType: 'blob' })).data;
  }

  /** PDF des QR de places (un par rack actif), à coller sur les tables. */
  async deskQrPdf(): Promise<Blob> {
    return (await api.get('/camera/racks/desk-qr/', { responseType: 'blob' })).data;
  }
}

const rackService = new RackService();
export default rackService;
